import { useApi } from '@api/ApiProvider'
import { ApiError } from '@api/client'
import { useI18n } from '@i18n/I18nProvider'
import type { MessageKey } from '@i18n/messages'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@ui/Button'
import { Screen } from '@ui/Screen'
import { type FormEvent, useState } from 'react'
import { useStaffSession } from './StaffSession'

// Mensaje de un intento fallido: la API no dice si el correo existe (spec `staff-auth`).
export const loginErrorKey = (error: unknown): MessageKey => {
	if (error instanceof ApiError && error.offline) return 'common.offline'
	if (error instanceof ApiError && error.status === 401) return 'login.invalid'
	if (error instanceof ApiError && error.status === 429) return 'login.tooMany'
	return 'login.error'
}

const FIELD = 'min-h-12 w-full rounded-2xl border-2 border-earth-300 bg-white px-4 text-lg focus:border-forest-700'

// Acceso del personal de la asociación al panel. Las cuentas se crean en el servidor.
export const LoginPage = () => {
	const { t } = useI18n()
	const api = useApi()
	const { signIn } = useStaffSession()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [missing, setMissing] = useState(false)
	const login = useMutation({
		mutationFn: () => api.login(email.trim(), password),
		onSuccess: signIn,
	})

	const submit = (event: FormEvent) => {
		event.preventDefault()
		const incomplete = email.trim() === '' || password === ''
		setMissing(incomplete)
		if (!incomplete) login.mutate()
	}

	const error = missing ? 'login.missing' : login.error ? loginErrorKey(login.error) : null
	return (
		<Screen title={t('login.title')}>
			<p className="mb-6 text-earth-800">{t('login.hint')}</p>
			<form onSubmit={submit} className="flex flex-col gap-5" noValidate>
				<label className="flex flex-col gap-2 font-semibold">
					{t('login.email')}
					<input
						type="email"
						autoComplete="username"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						className={FIELD}
					/>
				</label>
				<label className="flex flex-col gap-2 font-semibold">
					{t('login.password')}
					<input
						type="password"
						autoComplete="current-password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						className={FIELD}
					/>
				</label>
				{error && (
					<p role="alert" className="rounded-2xl bg-damaged/10 p-4 font-semibold text-damaged">
						{t(error)}
					</p>
				)}
				<Button type="submit" fullWidth disabled={login.isPending}>
					{login.isPending ? t('login.sending') : t('login.submit')}
				</Button>
			</form>
		</Screen>
	)
}
